import SvgIcon from 'components/Globals/Base/SvgIcon';
import useBreakpoint from 'hooks/useBreakpoint';
import SpinningLoader from 'components/Loader/SpinningLoader';
import { useEffect, useState } from 'react';
import { PROFILE_SECTION_TEST_IDS } from 'constants/testsIds/profileSection';
import {
  CurrentSubscriptionsContainer,
  LoaderWrapper,
  PianoResource,
  ProfileSummaryContainer,
  RenewLink,
  SubscriptionStatus,
  TopLabelsContainer,
  UserCardTitle,
} from './ProfileSummaryStyles';

export interface ProfileSummaryInfo {
  firstName?: string;
  lastName?: string;
  pianoAccessName?: string;
  subscriptionInfo?: { status: string; daysLeft: number };
  className?: string;
  accesses: string[];
  email?: string;
  newslettersCount?: number | null;
  isLoading?: boolean;
}

export interface ProfileSummaryProps {
  profileSummary: ProfileSummaryInfo;
}

export const subscriptionStatusMapper = (status: string, daysLeft: number) => {
  const dayOrDays = daysLeft === 1 ? 'Day' : 'Days';
  return {
    active: 'Subscription Active',
    cancelled: 'Subscription Cancelled',
    expired: 'Subscription Expired',
    expiring: `*${daysLeft} ${dayOrDays} Left`,
    inactive: 'Subscription Inactive',
  }[status];
};

const ProfileSummary = ({ profileSummary }: ProfileSummaryProps) => {
  const isMobile = useBreakpoint('mdMax');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (profileSummary.email && !profileSummary.isLoading) {
      setIsLoading(false);
    }
  }, [profileSummary.email, profileSummary.isLoading]);

  const subDaysLeft = profileSummary?.subscriptionInfo?.daysLeft || 0;
  const subscriptionExpiringSoon = subDaysLeft !== 0 && subDaysLeft < 8;

  const subStatus = subscriptionExpiringSoon ? 'expiring' : profileSummary?.subscriptionInfo?.status || '';
  const subscriptionStatus = subscriptionStatusMapper(subStatus, subDaysLeft);

  const getAccessesDisplayText = () => {
    const accesses = profileSummary?.accesses || [];
    const uniqueAccesses = [...new Set(accesses)].join(', ');
    const hasAccesses = accesses.length > 0;
    const newslettersCount = profileSummary?.newslettersCount;

    if (hasAccesses) {
      if (newslettersCount) {
        return `${uniqueAccesses}, `;
      }
      return uniqueAccesses;
    }
    if (!newslettersCount) {
      return '-';
    }

    return '';
  };

  return isLoading && isMobile ? (
    <LoaderWrapper>
      <SpinningLoader
        show={isLoading}
        className='loader'
      />
    </LoaderWrapper>
  ) : (
    <ProfileSummaryContainer
      className={`profile-summary ${profileSummary.className}`}
      data-cy={`${PROFILE_SECTION_TEST_IDS.PROFILE_SUMMARY_CONTAINER}`}
    >
      <TopLabelsContainer data-cy={`${PROFILE_SECTION_TEST_IDS.TOP_LABELS_CONTAINER}`}>
        <PianoResource data-cy={`${PROFILE_SECTION_TEST_IDS.PIANO_RESOURCE}`}>
          {profileSummary?.pianoAccessName}
        </PianoResource>
        <SubscriptionStatus
          className={`${subStatus} ${subscriptionExpiringSoon ? 'soon' : ''}`}
          data-cy={`${PROFILE_SECTION_TEST_IDS.SUBSCRIPTION_STATUS}`}
        >
          {subscriptionStatus}
        </SubscriptionStatus>
        {profileSummary?.subscriptionInfo?.status === 'expiring' && (
          <RenewLink
            href='/subscribe/?utm_content=winback'
            data-cy={`${PROFILE_SECTION_TEST_IDS.RENEW_LINK}`}
          >
            Renew <SvgIcon name='Refresh' />
          </RenewLink>
        )}
      </TopLabelsContainer>
      <UserCardTitle data-cy={`${PROFILE_SECTION_TEST_IDS.USER_CARD_TITLE}`}>
        {profileSummary?.firstName} {profileSummary?.lastName}
      </UserCardTitle>
      <CurrentSubscriptionsContainer data-cy={`${PROFILE_SECTION_TEST_IDS.CURRENT_SUBSCRIPTIONS_CONTAINER}`}>
        <p data-cy={`${PROFILE_SECTION_TEST_IDS.CURRENT_SUBSCRIPTIONS_LABEL}`}>Your Current Accesses:</p>
        {getAccessesDisplayText()}
        {!!profileSummary.newslettersCount &&
          `Subscribed to ${profileSummary?.newslettersCount} 
        ${profileSummary?.newslettersCount === 1 ? 'Newsletter' : 'Newsletters'} `}
      </CurrentSubscriptionsContainer>
    </ProfileSummaryContainer>
  );
};

export default ProfileSummary;
